import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { StepperLabel } from "./StepperLabel.js";
import { StepperMarker, type StepStatus } from "./StepperMarker.js";
import type { StepItem, StepperProps } from "./Stepper.types.js";

/* ---------------- Stepper (C8 #7 — process indicator) ----------------
   A multi-step progress indicator (strategy §15, §24). Items-driven, like
   Tabs/Select/RadioGroup. There is no formal APG "stepper" pattern, so we use
   the idiomatic semantics: an ordered list (<ol>) with aria-current="step" on
   the active step and an sr-only status word ("completed/current/error") on
   each, so the order and progress are conveyed without color alone (§9).

   Per-step status is derived from the current step: before it → complete,
   at it → current, after it → upcoming; an `errorStep` overrides to error.
   `completed` sets completion independently. With `clickable`, completed
   steps render as buttons so the user can step back; upcoming steps are
   never navigable unless `clickable="all"`. Horizontal (default) or vertical.
   `mode="static"` is an explainer, not progress: it has no status at all. */

export type { StepItem, StepperProps };

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/40 active:opacity-80";
/* A swatch disc is the button: the one focus ring, a small lift on hover and
   a dip on press. */
const SWATCH_HIT =
  "rounded-full focus-ring transition-transform hover:scale-105 active:scale-95";

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(
  function Stepper(
    {
      steps,
      value,
      defaultValue,
      onValueChange,
      orientation = "horizontal",
      clickable = false,
      errorStep,
      size = "md",
      labelHidden = false,
      variant = "numbered",
      tone,
      connector: connectorLength = "fill",
      mode = "progress",
      completed,
      "aria-label": ariaLabel,
      className,
      ...rest
    },
    ref,
  ) {
    const isControlled = value !== undefined;
    const [internal, setInternal] = React.useState(
      defaultValue ?? (completed ? "" : (steps[0]?.value ?? "")),
    );
    const current = isControlled ? (value as string) : internal;
    const isStatic = mode === "static";
    const done = completed && !isStatic ? new Set(completed) : undefined;
    const found = steps.findIndex((s) => s.value === current);
    // Static has no current step; with `completed`, an unmatched value has none.
    const currentIndex = isStatic ? -1 : done ? found : Math.max(0, found);
    const vertical = orientation === "vertical";
    const swatch = variant === "swatch";
    // Swatches and short connectors size the row to its steps.
    const fixedRow = swatch || connectorLength === "short";

    const go = (v: string) => {
      if (!isControlled) setInternal(v);
      onValueChange?.(v);
    };

    const isDone = (i: number, step: StepItem) =>
      done ? done.has(step.value) : i < currentIndex;

    const statusOf = (i: number, step: StepItem): StepStatus => {
      if (isStatic) return "static";
      if (errorStep && step.value === errorStep) return "error";
      if (i === currentIndex) return "current";
      if (isDone(i, step)) return "complete";
      return "upcoming";
    };

    return (
      <ol
        ref={ref}
        aria-label={ariaLabel}
        className={cn(
          "flex",
          vertical ? "flex-col" : swatch ? "items-center gap-1" : "items-start",
          className,
        )}
        {...rest}
      >
        {steps.map((step, i) => {
          const status = statusOf(i, step);
          const isLast = i === steps.length - 1;
          const navigable =
            !!onValueChange &&
            !isStatic &&
            (clickable === "all" ||
              (clickable === true && status === "complete"));

          const hit = (node: React.ReactElement, shape: string) =>
            navigable ? (
              <button
                type="button"
                onClick={() => go(step.value)}
                className={swatch ? SWATCH_HIT : `${shape} ${FOCUS}`}
              >
                {node}
              </button>
            ) : (
              node
            );
          const tip = (node: React.ReactElement) =>
            labelHidden ? <Tooltip content={step.label}>{node}</Tooltip> : node;

          const indicator = (
            <StepperMarker
              status={status}
              index={i}
              size={size}
              marker={step.marker}
              color={step.color}
              variant={variant}
              tone={tone}
            />
          );
          const labelBlock = (
            <StepperLabel
              step={step}
              status={status}
              srOnly={labelHidden}
              vertical={vertical}
            />
          );
          const inner = (
            <span className="flex items-center gap-2">
              {indicator}
              {labelBlock}
            </span>
          );
          const connector = !isLast && !swatch && (
            <span
              aria-hidden="true"
              className={cn(
                vertical
                  ? "w-px flex-1 my-1 min-h-4"
                  : connectorLength === "short"
                    ? cn("h-px mx-1 shrink-0", size === "sm" ? "w-3" : "w-5")
                    : "h-px flex-1 mx-2 min-w-4",
                isDone(i, step)
                  ? tone === "info"
                    ? "bg-pb-cyan/50"
                    : "bg-pb-green"
                  : "bg-pb-border",
              )}
            />
          );

          return (
            <li
              key={step.value}
              aria-current={status === "current" ? "step" : undefined}
              className={cn(
                "flex",
                vertical ? "flex-col" : "items-center",
                !vertical && !isLast && !fixedRow && "flex-1",
              )}
            >
              {vertical ? (
                <span className="flex gap-3">
                  <span className="flex flex-col items-center">
                    {tip(hit(labelHidden ? inner : indicator, "rounded-full"))}
                    {connector}
                  </span>
                  {!labelHidden && hit(labelBlock, "text-start rounded")}
                </span>
              ) : (
                <>
                  {tip(hit(inner, labelHidden ? "rounded-full" : "rounded"))}
                  {connector}
                </>
              )}
            </li>
          );
        })}
      </ol>
    );
  },
);
